import { ENTRY_POINT, SOURCE, type AppConfig, type Source } from "@/config"
import { ALLOWED_SOURCES as FACE_180_ALLOWED_SOURCES } from "@/features/presets/face-180"
import { PRESET, REQUIRED_LIGHTING } from "@/features/presets/params"
import { TUTORIAL_KIND } from "@/features/tutorial/Tutorial.svelte"
import { checkDesktop } from "@/libs/hardware/desktop"
import { compose } from "@/libs/lodash"
import { getContext, setContext } from "svelte"
import { derived, writable } from "svelte/store"

const DEFAULT_PRESET = PRESET.FACE
const DEFAULT_SOURCE = SOURCE.FRONT_CAMERA
const RULES_BY_SOURCE = { [SOURCE.COMPANION]: checkDesktop }
const ALLOWED_SOURCES_BY_PRESET = { [PRESET.FACE_180]: FACE_180_ALLOWED_SOURCES }
const DEFAULT_QUALITY_CHECK_SOURCES = [
  SOURCE.FRONT_CAMERA,
  SOURCE.BACK_CAMERA,
  SOURCE.UPLOAD,
  SOURCE.COMPANION,
]

type RequiredConfigParams =
  | "preset"
  | "sources"
  | "effects"
  | "capture"
  | "tutorial"
  | "requiredLighting"
  | "qualityCheckSources"

const createApplicationStore = (config: AppConfig) => {
  const store = writable({ config: getAdjustedConfig(config) })

  const addSources = (sources: Source[]) =>
    store.update(({ config, ...state }) => {
      const { preset, sources: currentSources } = config
      const validator = validateSources({
        preset,
        allowedSourcesByPreset: ALLOWED_SOURCES_BY_PRESET,
      })

      return {
        ...state,
        config: {
          ...config,
          sources: [
            ...currentSources,
            ...validator(sources.filter((source) => !currentSources.includes(source))),
          ],
        },
      }
    })

  const removeSources = (sources: Source[]) =>
    store.update(({ config, ...state }) => ({
      ...state,
      config: {
        ...config,
        sources: config.sources.filter((source) => !sources.includes(source)),
      },
    }))

  const refresh = (config: AppConfig) =>
    store.update((state) => ({ ...state, config: getAdjustedConfig(config) }))
  return {
    store: { ...derived(store, ($store) => $store), addSources, removeSources },
    refresh,
  }
}

const APPLICATION_STORE_CONTEXT_KEY = Symbol("applicationStore")

type ApplicationStore = ReturnType<typeof createApplicationStore>["store"]

export const getApplicationStore = () => getContext<ApplicationStore>(APPLICATION_STORE_CONTEXT_KEY)
export const initApplicationStore = (config: AppConfig) => {
  const store = createApplicationStore(config)

  setContext<ApplicationStore>(APPLICATION_STORE_CONTEXT_KEY, store.store)

  return store
}

const getAdjustedConfig = (
  config: AppConfig,
): Omit<AppConfig, RequiredConfigParams> & Required<Pick<AppConfig, RequiredConfigParams>> => {
  const cfg = { ...config, preset: getAdjustedPreset(config) }

  return {
    ...cfg,
    sources: getAdjustedSources(cfg),
    requiredLighting: getAdjustedRequiredLighting(cfg),
    effects: cfg.effects ?? false,
    tutorial: cfg.tutorial ?? TUTORIAL_KIND.VIDEO,
    entryPoint: cfg.entryPoint ?? ENTRY_POINT.CAPTURE,
    capture: cfg.capture ?? (cfg.preset === PRESET.HAIR ? "manual" : "auto"),
    qualityCheck: cfg.qualityCheck ?? "off",
    qualityCheckSources: cfg.qualityCheckSources ?? DEFAULT_QUALITY_CHECK_SOURCES,
  }
}

/** Checks if the preset is valid and returns default preset otherwise */
const getAdjustedPreset = ({ preset }: AppConfig) => {
  if (preset && Object.values(PRESET).includes(preset)) return preset

  console.warn(`[LIQA] Warning: Unknown preset "${preset}". Defaulting to "${DEFAULT_PRESET}".`)

  return DEFAULT_PRESET
}

/** Checks for requiredLighting parameter and, if it is absent, adapts settings attribute */
const getAdjustedRequiredLighting = ({ requiredLighting, settings }: AppConfig) => {
  if (requiredLighting) return requiredLighting

  if (settings)
    console.warn(
      '[LIQA] Warning: "settings" attribute is deprecated. Use "requiredLighting" instead.',
    )

  if (settings?.lighting !== 0) return REQUIRED_LIGHTING.DEFAULT

  return REQUIRED_LIGHTING.NONE
}

const getAdjustedSources = ({ sources = [], preset }: AppConfig) =>
  compose(
    filterSourcesByRules(RULES_BY_SOURCE),
    provideDefaultSources,
    validateSources({ preset, allowedSourcesByPreset: ALLOWED_SOURCES_BY_PRESET }),
    oldSourcesToNewSources,
  )(sources)

const validateSources = <P extends string, A extends Partial<Record<P, Source[]>>>({
  allowedSourcesByPreset,
  preset,
}: {
  allowedSourcesByPreset: A
  preset?: P
}) => {
  const knownSources = new Set(Object.values(SOURCE))
  const allowedSources = preset && allowedSourcesByPreset[preset]

  return <T extends string[]>(sources: T): Source[] => {
    const adjustedSources = sources.filter((source) => {
      if (!knownSources.has(source as Source)) {
        console.warn(`[LIQA] Warning: Unknown source "${source}".`)

        return false
      }

      if (!allowedSources) return true

      const isAllowedSource = allowedSources.includes(source as Source)

      if (!isAllowedSource)
        console.warn(
          `[LIQA] Warning: The "${preset}" preset doesn't support the "${source}" source.`,
        )

      return isAllowedSource
    }) as Source[]

    if (!allowedSources || adjustedSources.length > 0) return adjustedSources

    const defaultSources = allowedSources.slice(0, 1)

    console.warn(
      `[LIQA] Warning: No valid sources were provided for the selected preset ("${preset}"). ` +
        (defaultSources.length > 0
          ? `Defaulting to [${defaultSources.map((s) => `"${s}"`).join(", ")}]. `
          : "") +
        "Please check the presets and image sources documentation and make sure you are using the correct sources. https://docs.liqa.haut.ai/customization/user-flow/image-sources.html",
    )

    return defaultSources
  }
}
const filterSourcesByRules =
  (rules: Partial<Record<Source, () => boolean>>) => (sources: Source[]) =>
    sources.filter((source) => rules[source]?.() ?? true)
/** Provides default source if none were provided */
const provideDefaultSources = (sources: Source[]) =>
  sources.length === 0 || !sources.some((s) => s !== SOURCE.COMPANION)
    ? [...sources, DEFAULT_SOURCE]
    : sources
/** Converts old sources to modern providing backward compatibility and warns of deprecated props. */
const oldSourcesToNewSources = <T extends string[]>(sources: T): Source[] => {
  const DEPRECATED_SOURCES_MAP = {
    photo: SOURCE.UPLOAD,
    camera: SOURCE.FRONT_CAMERA,
  }

  return sources.map((source) => {
    const modernSource = DEPRECATED_SOURCES_MAP[source as keyof typeof DEPRECATED_SOURCES_MAP]

    if (!modernSource) return source as Source

    console.warn(
      `[LIQA] Deprecation warning: the "${source}" source has been deprecated. Use "${modernSource}" instead.`,
    )

    return modernSource
  })
}
